import { Collapse, Form } from "antd"
import { Group, Icon, notification, RadioButtons, Spinner, Text, Title } from "components/common"
import { t } from "i18next"
import theme from "utils/themeVars"
import FormItemCreator from "../../formItemCreator"
import { createFormItemConfig, isPaymentProofUploading, PAYMENTS_FORM_LIST_NAME } from "../utils/paymentUtils"
import {
  cardPaymentFormChildren,
  cardPaymentFormProps,
  chequeFormChildren,
  chequeFormProps,
  depositSlipFormChildren,
  depositSlipFormProps,
  onlinePaymentFormChildren,
  onlinePaymentFormProps,
  payorderFormChildren,
  payorderFormProps,
} from "features/leads/components/addSalesModal/utils/formItemConstants"
import { useAddSalesContext } from "features/leads/components/addSalesModal/addSalesContext/addSalesContext"
import { useSelector } from "react-redux"
import { useCallback, useMemo } from "react"
import { debounce } from "lodash"

const PaymentDetailsCollapse = ({ ocrEnabled, isLoading, paymentProof, depositSlip, listFieldName }) => {
  const { stateManager, formWatchers, addSalesApis, form } = useAddSalesContext()
  const { project } = formWatchers
  const {
    leadSalesInfo,
    referenceError,
    modeOfPayments,
    getBankName,
    getPaymentMode,
    referenceValidity,
    paymentModesMap,
    banks,
    paymentMode,
    isLoadingPaymentMode,
    validateReference,
  } = addSalesApis

  const fullPrefix = useMemo(() => [PAYMENTS_FORM_LIST_NAME, listFieldName], [listFieldName])
  const modeOfPayment = Form.useWatch([...fullPrefix, "modeOfPayment"], form)
  const payorderBank = Form.useWatch([...fullPrefix, "payorderBank"], form)
  const chequeBank = Form.useWatch([...fullPrefix, "chequeBank"], form)

  const userPlatformKey = useSelector(state => state.app.user?.info?.AgencyConfiguration?.Platform?.platformKey)

  const hasPaymentProof =
    stateManager?.project?.paymentProof || project?.paymentProof || leadSalesInfo?.project?.paymentProof

  const optionList = useMemo(() => ({ bank: banks, paymentMode }), [banks, paymentMode])

  const flushReferenceValidation = useCallback(
    value => {
      validateReference(value)
        .unwrap()
        .then(() => form.validateFields([[...fullPrefix, "referenceNumber"]]))
        .catch(e => notification?.error({ message: e?.error }))
    },
    [validateReference, form, fullPrefix]
  )

  const debouncedReferenceValidator = useMemo(
    () => debounce(flushReferenceValidation, 500),
    [flushReferenceValidation]
  )

  const scopedGetFieldValue = useCallback(name => form.getFieldValue([...fullPrefix, name]), [fullPrefix, form])
  const scopedSetFieldsValue = useCallback(
    values => {
      Object.keys(values || {}).forEach(key => form.setFieldValue([...fullPrefix, key], values[key]))
    },
    [fullPrefix, form]
  )

  const formItemConfig = useMemo(
    () =>
      createFormItemConfig({
        modeOfPayment,
        paymentProof,
        hasPaymentProof,
        setFieldsValue: scopedSetFieldsValue,
        getFieldValue: scopedGetFieldValue,
        optionList,
        debouncedReferenceValidator,
        referenceError,
        referenceValidity,
        userPlatformKey,
        payorderBank,
        chequeBank,
        formItemConstants: {
          cardPaymentFormProps,
          cardPaymentFormChildren,
          payorderFormProps,
          payorderFormChildren,
          depositSlipFormProps,
          depositSlipFormChildren,
          onlinePaymentFormProps,
          onlinePaymentFormChildren,
          chequeFormProps,
          chequeFormChildren,
        },
      }),
    [
      modeOfPayment,
      payorderBank,
      chequeBank,
      paymentProof,
      hasPaymentProof,
      scopedSetFieldsValue,
      scopedGetFieldValue,
      optionList,
      debouncedReferenceValidator,
      referenceError,
      referenceValidity,
      userPlatformKey,
    ]
  )

  const getHeaderTitle = () => {
    if (!ocrEnabled) return t("Payment Details")
    if (isPaymentProofUploading(paymentProof)) return t("Uploading payment proof...")
    if (isLoading || isLoadingPaymentMode) return t("AI is fetching details from the payment proof you have uploaded")
    return paymentProof?.imageData?.paymentMode
      ? `${t("AI Detected Details; ")} ${paymentProof?.imageData?.paymentMode} `
      : t("Payment Details")

  }

  return (
    <Collapse
      defaultActiveKey={[0]}
      style={{ "--ant-collapse-header-bg": theme["accent-color-light"], "--ant-color-border": theme["accent-color"] }}
      expandIconPosition="end"
      expandIcon={({ isActive }) =>
        isLoading || isLoadingPaymentMode || isPaymentProofUploading(paymentProof) ? (
          <Spinner type="plain" size="small" style={{ color: theme["accent-color"] }} />
        ) : (
          <Icon icon="HiChevronDown" size="1.6em" style={{ transform: `rotate(${isActive ? 180 : 0}deg)` }} />
        )
      }
    >
      <Collapse.Panel
        key={1}
        header={
          <Title level={5} className="mbe-0" style={{ color: theme["accent-color"] }}>
            {getHeaderTitle()}
          </Title>
        }
      >
        <Text type="secondary" className="mbe-20">
          {!ocrEnabled
            ? t("Please select a payment mode.")
            : t("Details detected from the payment proof are shown below. You may update them if required.")}
        </Text>
        <Group className="span-all" template="initial" gap="8px">
          {(!!project || !!leadSalesInfo?.projectName) && (
            <Form.Item
              className="mbe-0"
              preserve
              required
              name={[listFieldName, "modeOfPayment"]}
              label={t("Mode Of Payment")}
              rules={[{ required: true, message: t("Mode of payment is required") }]}
            >
              <RadioButtons
                getValue={item => item?.key}
                options={modeOfPayments}
                onChange={value => {
                  const option = paymentModesMap?.[value?.target?.value]?.meta
                  !!option && getPaymentMode(option?.id, true)
                  if (option?.getBankNames) getBankName({}, true)
                }}
              />
            </Form.Item>
          )}
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              if (!getFieldValue([...fullPrefix, "modeOfPayment"])) return null

              const mode = ocrEnabled
                ? paymentProof?.paymentModes?.find(item => item?.key === modeOfPayment)
                : paymentModesMap?.[modeOfPayment]?.meta

              const config = formItemConfig[mode?.key]
              const depositConfig = formItemConfig["bank_deposit_slip"]
              const attachment = mode?.PaymentModeAttachmentTypeMappings?.find(
                item => item?.AttachmentType?.key === "bank_deposit_slip"
              )

              const showMainFields = (!ocrEnabled || config?.conditionToCheckRender) && config?.extraCondition
              const showDepositFields =
                (attachment?.AttachmentType?.key === "bank_deposit_slip" && depositConfig?.conditionToCheckRender) ||
                mode?.key === "cash"

              return (
                <>
                  {!!showMainFields && (
                    <FormItemCreator
                      template="repeat(2, 1fr)"
                      formItemPropList={config?.formItemPropList?.map(item => ({
                        ...item,
                        name: [listFieldName, item.name],
                      }))}
                      formItemChildrenList={config?.formItemChildrenList}
                    />
                  )}
                  {!!showDepositFields &&
                    (depositSlip?.imageData || (!ocrEnabled && depositSlip?.fileList?.length)) && (
                      <>
                        <Title type="secondary" level={4}>
                          {t("Bank Deposit Slip Details")}
                        </Title>

                        <FormItemCreator
                          template="repeat(2, 1fr)"
                          formItemPropList={depositConfig?.formItemPropList?.map(item => ({
                            ...item,
                            name: [listFieldName, item.name],
                          }))}
                          formItemChildrenList={depositConfig?.formItemChildrenList}
                        />
                      </>
                    )}
                </>
              )
            }}
          </Form.Item>
        </Group>
      </Collapse.Panel>
    </Collapse>
  )
}

export default PaymentDetailsCollapse
